import { Inject, Injectable, InjectionToken } from '@angular/core';
import JSEncrypt from 'jsencrypt';
import { environment } from '@env/environment';
import { EncryptionDomain } from '@app/config/encrypted-services.config';
import { EncryptionError } from './encryption.service';

/**
 * Pública RSA del BFF por dominio (PEM en Base64 de una línea, como `checkinBffP`
 * en CUSS). Es pública, no secreta; se guarda sin los marcadores BEGIN/END para no
 * disparar falsos positivos de los escáneres de secretos.
 */
export const BFF_P = new InjectionToken<Partial<Record<EncryptionDomain, string>>>('BFF_P', {
  providedIn: 'root',
  factory: () => ({
    checkin: environment.publics?.checkinBffP,
    payment: environment.publics?.paymentBffP,
  }),
});

/** Tamaño del par RSA del kiosko. Solo las pruebas lo bajan (keygen más rápido). */
export const RSA_KEY_BITS = new InjectionToken<number>('RSA_KEY_BITS', {
  providedIn: 'root',
  factory: () => 2048,
});

/** Espera máxima para pregenerar el par cuando el navegador no queda ocioso. */
const PRELOAD_TIMEOUT_MS = 3000;

/**
 * Parte RSA del esquema híbrido (contrato del BFF, igual que CUSS):
 *  - El kiosko genera un par RSA-2048 una sola vez por sesión. La pública viaja en
 *    Base64 en el header `x-client-public-key` y el BFF la usa para cifrar la llave
 *    AES de la respuesta (RSA PKCS#1 v1.5).
 *  - Para cifrar el request se usa la pública FIJA del BFF (`BFF_P`), no la del kiosko.
 *
 * WebCrypto no soporta cifrado RSA PKCS#1 v1.5 (solo RSA-OAEP), por eso se usa JSEncrypt.
 * El keygen de JSEncrypt es síncrono y bloquea el hilo principal (0.5 a 3 s en un
 * kiosko): `preload()` lo adelanta cuando la app queda ociosa, antes del primer request.
 */
@Injectable({
  providedIn: 'root',
})
export class RsaKeyService {
  private keypair?: Promise<JSEncrypt>;

  constructor(
    @Inject(BFF_P) private readonly _bffP: Partial<Record<EncryptionDomain, string>>,
    @Inject(RSA_KEY_BITS) private readonly _keyBits: number
  ) {}

  /** Pregenera el par de la sesión cuando el navegador queda ocioso; no lanza errores. */
  preload(): void {
    const start = () => void this.ensureKeypair().catch(() => undefined);
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(start, { timeout: PRELOAD_TIMEOUT_MS });
    } else {
      setTimeout(start, 0);
    }
  }

  /** Pública del par de la sesión: PEM en Base64 (valor del header x-client-public-key). */
  async getPublicKeyBase64(): Promise<string> {
    const rsa = await this.ensureKeypair();
    return btoa(rsa.getPublicKey());
  }

  /** Descifra con la privada de la sesión la llave AES que manda el BFF en la respuesta. */
  async decryptAesKey(cipherB64: string): Promise<string> {
    const rsa = await this.ensureKeypair();
    const aesKeyB64 = safely(() => rsa.decrypt(cipherB64));
    if (!aesKeyB64) {
      throw new EncryptionError('RSA_DECRYPT_FAILED', 'No se pudo descifrar la llave AES');
    }
    return aesKeyB64;
  }

  /** Cifra `plainText` (la llave AES del request) con la pública fija del BFF del dominio. */
  async encryptForBff(domain: EncryptionDomain, plainText: string): Promise<string> {
    const pem = this.bffPem(domain);
    const cipherB64 = safely(() => {
      const rsa = new JSEncrypt();
      rsa.setPublicKey(pem);
      return rsa.encrypt(plainText);
    });
    if (!cipherB64) {
      throw new EncryptionError(
        'RSA_ENCRYPT_FAILED',
        `No se pudo cifrar con la pública del BFF (${domain})`
      );
    }
    return cipherB64;
  }

  private bffPem(domain: EncryptionDomain): string {
    const pemB64 = (this._bffP?.[domain] || '').replace(/\s+/g, '');
    if (!pemB64) {
      throw new EncryptionError('KEY_MISSING', `Falta la pública del BFF (${domain})`);
    }
    try {
      return atob(pemB64);
    } catch {
      throw new EncryptionError(
        'KEY_INVALID',
        `La pública del BFF no es Base64 válido (${domain})`
      );
    }
  }

  private ensureKeypair(): Promise<JSEncrypt> {
    if (!this.keypair) {
      this.keypair = this.generateKeypair();
      // Si falla no se cachea, para que el siguiente request vuelva a intentar.
      this.keypair.catch(() => (this.keypair = undefined));
    }
    return this.keypair;
  }

  private async generateKeypair(): Promise<JSEncrypt> {
    // Cede un tick antes del keygen (síncrono) para no bloquear el request en curso.
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const rsa = new JSEncrypt({ default_key_size: String(this._keyBits) });
      rsa.getKey();
      return rsa;
    } catch {
      throw new EncryptionError('RSA_KEYGEN_FAILED', 'No se pudo generar el par de llaves RSA');
    }
  }
}

/** JSEncrypt devuelve `false` en la mayoría de los fallos, pero con entradas malformadas lanza. */
function safely(operation: () => string | false): string | false {
  try {
    return operation();
  } catch {
    return false;
  }
}
